// TexasSeat.jsx - Individual player view
import React, { useState, useCallback, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import GameSeat from "../../components/GameSeat";
import { leaveSeat } from "../../services/SeatService";
import { placeBet, fold, requestExercise, check, setReady } from "../../services/TexasService";
import { postDonationSession } from "../../services/AnimalSplitCore";
import { useRoomRealtime } from "../../hooks/UseRoomRealtime";
import { getStableAnimalEmoji } from "../../utils/utils";
import "./TexasSeat.css";

const TexasSeat = () => {
  const { room_id, seat_index } = useParams();
  const history = useHistory();
  const parsedSeatIndex = parseInt(seat_index);
  const seatIndexNum = Number.isNaN(parsedSeatIndex) ? null : parsedSeatIndex;

  const handleSeatOccupied = useCallback(() => {
    alert("This seat is already open in another tab or device.");
    history.push(`${process.env.PUBLIC_URL}/games/texas/${room_id}`);
  }, [history, room_id]);

  const { room, loading, error, seatPresence } = useRoomRealtime(room_id, {
    seatIndex: seatIndexNum,
    onSeatOccupied: handleSeatOccupied,
  });
  const [betAmount, setBetAmount] = useState("");
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [showRoundSummary, setShowRoundSummary] = useState(false);
  const [roundSummary, setRoundSummary] = useState(null);
  const [lastSummaryKey, setLastSummaryKey] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [autoWaitingApplied, setAutoWaitingApplied] = useState(false);

  const exerciseOptions = [
    { name: "2 min plank", icon: "🤸", description: "Hold plank position" },
    { name: "50 squats", icon: "🦵", description: "Full depth squats" },
    { name: "30 push ups", icon: "💪", description: "Standard push ups" },
    { name: "30 overhead press", icon: "🏋️", description: "Shoulder press" }
  ];

  const handleBackToRoom = () => {
    history.push(`${process.env.PUBLIC_URL}/games/texas/${room_id}`);
  };

  useEffect(() => {
    if (room?.data?.roundSummary && room.data.roundSummary.length > 0) {
      const summaryKey = JSON.stringify(room.data.roundSummary);
      if (summaryKey !== lastSummaryKey) {
        setRoundSummary(room.data.roundSummary);
        setShowRoundSummary(true);
        setLastSummaryKey(summaryKey);
      }
    }
  }, [room?.data?.roundSummary, lastSummaryKey]);

  useEffect(() => {
    if (!room?.data?.turn?.endsAt) return;
    const id = setInterval(() => setNowMs(Date.now()), 100);
    return () => clearInterval(id);
  }, [room?.data?.turn?.endsAt]);

  const handleLeaveSeat = async () => {
    if (seatIndexNum === null) return;
    
    const confirmLeave = window.confirm("Are you sure you want to leave this seat?");
    if (!confirmLeave) return;

    try {
      await leaveSeat(room_id, seatIndexNum);
      // Redirect back to room view
      history.push(`${process.env.PUBLIC_URL}/games/texas/${room_id}`);
    } catch (error) {
      console.error("Failed to leave seat:", error);
      alert("Failed to leave seat");
    }
  };

  const handlePlaceBet = async () => {
    if (seatIndexNum === null) return;
    const amount = parseInt(betAmount);
    
    if (!amount || amount <= 0) {
      alert("Please enter a valid bet amount");
      return;
    }

    if (!mySeat) {
      alert("Seat not found");
      return;
    }

    if (amount > mySeat.chips) {
      alert("Insufficient chips");
      return;
    }

    try {
      await placeBet(room_id, seatIndexNum, amount);
      setBetAmount("");
    } catch (error) {
      console.error("Failed to place bet:", error);
      alert(error.response?.data?.message || "Failed to place bet");
    }
  };

  const handleFold = async () => {
    if (seatIndexNum === null) return;
    if (!mySeat) return;
    try {
      await fold(room_id, seatIndexNum);
    } catch (error) {
      console.error("Failed to fold:", error);
      alert(error.response?.data?.message || "Failed to fold");
    }
  };

  const handleCheck = async () => {
    if (seatIndexNum === null) return;
    if (!mySeat) return;
    try {
      await check(room_id, seatIndexNum);
    } catch (error) {
      console.error("Failed to check:", error);
      alert(error.response?.data?.message || "Failed to check");
    }
  };

  const handleCall = async () => {
    if (seatIndexNum === null || !room?.data?.seats) return;
    if (!mySeat) return;
    if (!isMyTurn) return;

    // Calculate highest bet from all seats
    const highestBet = Math.max(...(room.data?.seats?.map(s => s.bet || 0) || [0]));
    // Calculate amount needed to call (difference between highest bet and current bet)
    const callAmount = Math.max(0, highestBet - (mySeat.bet || 0));
    if (callAmount <= 0) return;

    // Can't call more than available chips
    const actualCallAmount = Math.min(callAmount, mySeat.chips);
    if (actualCallAmount <= 0) return;

    try {
      await placeBet(room_id, seatIndexNum, actualCallAmount);
      setBetAmount("");
    } catch (error) {
      console.error("Failed to call:", error);
      alert(error.response?.data?.message || "Failed to call");
    }
  };

  const handleReadyCheckbox = async (nextReady) => {
    if (seatIndexNum === null) return;
    if (!mySeat?.playerName) return;
    if (room?.status === "playing") return;
    try {
      await setReady(room_id, seatIndexNum, Boolean(nextReady));
    } catch (error) {
      console.error("Failed to set ready:", error);
      alert(error.response?.data?.message || "Failed to set ready");
    }
  };

  const handleRequestExercise = async (exerciseType) => {
    if (seatIndexNum === null) return;
    try {
      await requestExercise(room_id, seatIndexNum, exerciseType);
      setShowExerciseModal(false);
      alert(`✅ Exercise request submitted: ${exerciseType}\nWait for admin approval to earn 1000 chips!`);
    } catch (error) {
      console.error("Failed to request exercise:", error);
      alert(error.response?.data?.message || "Failed to request exercise");
    }
  };

  const handleDonate = async (amount, chips) => {
    if (seatIndexNum === null) return;
    try {
      const mySeat = room.data?.seats?.[seatIndexNum];
      if (!mySeat || !mySeat.playerName) {
        alert("You must be seated to donate");
        return;
      }

      // Create donation session with poker game metadata
      const response = await postDonationSession({
        amount, // in cents (100, 500, 1000)
        alias: mySeat.playerName,
        note: `Texas Poker - Seat ${seatIndexNum + 1}`,
        returnUrl: window.location.href,
        roomId: room_id,
        seatPosition: seatIndexNum,
      });

      // Redirect to Stripe checkout
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error("Failed to create donation session:", error);
      alert("Failed to start donation process");
    }
  };

  // Find player's seat by seat_index
  const mySeat = seatIndexNum !== null 
    ? room?.data?.seats?.[seatIndexNum]
    : null;
  
  const otherPlayers = room?.data?.seats?.filter((seat, idx) => seat.playerName && idx !== seatIndexNum) || [];

  const turn = room?.data?.turn;
  const isMyTurn =
    room?.status === "playing" &&
    seatIndexNum !== null &&
    typeof turn?.seatIndex === "number" &&
    turn.seatIndex === seatIndexNum;

  const highestBet = Math.max(...(room?.data?.seats?.map((s) => s.bet || 0) || [0]));
  const canCheckNow = (mySeat?.bet || 0) === highestBet || (mySeat?.chips || 0) === 0;
  const callAmountNeeded = Math.max(0, highestBet - (mySeat?.bet || 0));
  const canCallNow = isMyTurn && callAmountNeeded > 0 && (mySeat?.chips || 0) > 0;

  const turnEndsAt = typeof turn?.endsAt === "number" ? turn.endsAt : Number(turn?.endsAt);
  const remainingMs = isMyTurn && !Number.isNaN(turnEndsAt) ? Math.max(0, turnEndsAt - nowMs) : 0;
  const remainingSec = isMyTurn ? Math.ceil(remainingMs / 1000) : null;
  const turnStartedAt = typeof turn?.startedAt === "number" ? turn.startedAt : Number(turn?.startedAt);
  const durationMs =
    isMyTurn && !Number.isNaN(turnEndsAt) && !Number.isNaN(turnStartedAt) && turnEndsAt > turnStartedAt
      ? turnEndsAt - turnStartedAt
      : 60000;
  const remainingPct = isMyTurn ? Math.max(0, Math.min(100, (remainingMs / durationMs) * 100)) : 0;

  const isRedSuit = (card) => {
    return card && (card.includes('♥') || card.includes('♦'));
  };

  const getResultLabel = (chipChange) => {
    if (chipChange > 0) return 'WIN';
    if (chipChange < 0) return 'LOSE';
    return 'PUSH';
  };

  const formatChipChange = (chipChange) => {
    if (chipChange > 0) return `+$${chipChange}`;
    if (chipChange < 0) return `-$${Math.abs(chipChange)}`;
    return '$0';
  };

  const getHandRankDisplay = (summary) => {
    if (!summary) return '--';
    if (summary.finalStatus === 'folded') {
      return summary.handRank ? `${summary.handRank} (folded)` : 'Folded';
    }
    return summary.handRank || '--';
  };

  const getBoardCards = () => {
    if (!roundSummary || roundSummary.length === 0) return [];
    return roundSummary[0].communityCards || [];
  };

  const shouldShowHoleCards = (summary) => {
    if (!summary) return false;
    if (summary.finalStatus === 'folded') return false;

    const winners = roundSummary?.filter(s => s.isWinner) || [];
    const everyoneElseFolded =
      winners.length === 1 &&
      summary.isWinner &&
      (roundSummary || []).every(s => s === summary || s.finalStatus === 'folded');

    if (everyoneElseFolded) return false;

    return summary.holeCards && summary.holeCards.length > 0;
  };

  const isSeatConnected = seatIndexNum !== null && seatPresence.includes(seatIndexNum);

  // When entering the seat page (lobby), ensure this seat starts in "waiting" (not sticky-ready).
  useEffect(() => {
    if (autoWaitingApplied) return;
    if (seatIndexNum === null) return;
    if (!mySeat?.playerName) return;
    if (room?.status === "playing") return;
    if (mySeat.status !== "ready") return;

    setAutoWaitingApplied(true);
    setReady(room_id, seatIndexNum, false).catch(() => {
      // best-effort; ignore
    });
  }, [autoWaitingApplied, room?.status, mySeat?.playerName, mySeat?.status, room_id, seatIndexNum]);

  return (
    <GameSeat
      room={room}
      loading={loading}
      error={error}
      seatIndex={seatIndexNum}
      onBack={handleBackToRoom}
    >
      {(roomData, seatData) => (
        <div className="texas-seat as-theme">
          {showRoundSummary && roundSummary && (
            <div className="round-summary-overlay">
              <div className="round-summary-box as-modal as-theme">
                <h2>🏁 Round Summary</h2>
                {getBoardCards().length > 0 && (
                  <div className="summary-board-top">
                    <span className="summary-hand-label">Board</span>
                    <div className="summary-card-row">
                      {getBoardCards().map((card, idx) => (
                        <div key={idx} className={`card ${isRedSuit(card) ? 'red-card' : ''}`}>
                          {card}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="round-summary-list">
                  {roundSummary.map((summary, index) => {
                    const resultLabel = getResultLabel(summary.chipChange);
                    const handRankDisplay = getHandRankDisplay(summary);
                    const showHole = shouldShowHoleCards(summary);
                    return (
                      <div
                        key={index}
                        className={`summary-item ${
                          summary.chipChange > 0 ? 'win' :
                          summary.chipChange < 0 ? 'loss' : 'neutral'
                        }`}
                      >
                        <div className="summary-info">
                          <div className="summary-top-row">
                            <span className="summary-seat">Seat {summary.seatPosition + 1}</span>
                            <span className={`summary-result-tag ${resultLabel.toLowerCase()}`}>
                              {resultLabel}
                            </span>
                          </div>
                          <div className="summary-name-row">
                            <span className="summary-name">
                              {summary.playerName}
                              {summary.isWinner && <span className="winner-badge"> 🏆 WINNER</span>}
                              {summary.finalStatus === 'folded' && (
                                <span className="summary-folded-tag">Folded</span>
                              )}
                            </span>
                            <span className="summary-change">{formatChipChange(summary.chipChange)}</span>
                          </div>
                          <div className="summary-hand-line">
                            <span className="summary-hand-label">Hand</span>
                            <span className="summary-hand-rank">{handRankDisplay}</span>
                          </div>
                          {showHole && (
                            <div className="summary-cards">
                              <div className="summary-hand-cards">
                                <span className="summary-hand-label">Hole</span>
                                <div className="summary-card-row">
                                  {summary.holeCards.map((card, idx) => (
                                    <div key={idx} className={`card ${isRedSuit(card) ? 'red-card' : ''}`}>
                                      {card}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  className="btn as-btn-primary-gradient"
                  onClick={() => {
                    setShowRoundSummary(false);
                    setRoundSummary(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}
          <div className="texas-seat-content">
        {/* Game Header */}
        <div className="game-header as-panel">
          <div className="as-panel-header">
            <div className="as-panel-title truncate">
              <span className="as-icon-badge" aria-hidden="true">♠️</span>
              <span className="truncate">{room.name}</span>
            </div>
            <div className="as-panel-actions">
              <span className={`connection-indicator ${isSeatConnected ? 'online' : 'offline'}`}>
                <span className={`status-dot ${isSeatConnected ? 'online' : 'offline'}`} />
                {isSeatConnected ? 'Connected' : 'Offline'}
              </span>
            </div>
          </div>
          <div className="as-panel-body">
            <div className="texas-animal-row">🐶🐱🐰🦊🐻🐼🐨🐯</div>
            <div className="game-info">
              <span>Blinds: ${room.data.smallBlind}/${room.data.bigBlind}</span>
              <span className="separator">|</span>
              <span className={`game-status ${room.status}`}>{room.status}</span>
              <span className="separator">|</span>
              <span>Pot: ${room.data.pot || 0}</span>
            </div>

            {/* Player info merged into header */}
            <div className={`seat-header-row ${isMyTurn ? "seat-header-row--turn" : ""}`}>
              <div className="seat-header-left">
                <div className="seat-header-name">
                  {getStableAnimalEmoji(`texas:${room_id}:seat:${seatIndexNum}:player:${mySeat.playerName}`)} {mySeat.playerName}
                </div>
                <div className="seat-header-meta">
                  <span className="seat-header-chip">💰 ${mySeat.chips}</span>
                  <span className="seat-header-dot">•</span>
                  <span className="seat-header-seat">Seat {mySeat.position + 1}</span>
                  {isMyTurn && <span className="seat-header-turn-tag">Your turn</span>}
                </div>
              </div>
              <div className="seat-header-right">
                <div className="seat-header-bet">
                  Bet: <strong>${mySeat.bet || 0}</strong>
                </div>
                {room.status !== "playing" && (
                  <label
                    className={`ready-checkbox ${mySeat.status === "ready" ? "ready-checkbox--checked" : ""}`}
                    title="When all connected occupied seats are ready, the game auto-starts"
                  >
                    <input
                      type="checkbox"
                      checked={mySeat.status === "ready"}
                      onChange={(e) => handleReadyCheckbox(e.target.checked)}
                    />
                    <span>Ready to play</span>
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main content wrapper for mobile reordering */}
        <div className="texas-seat-main-content">
        {/* Other Players Display */}
        <div className="other-players-section as-panel">
          <div className="as-panel-header">
            <div className="as-panel-title">
              <span className="as-icon-badge" aria-hidden="true">🧑‍🤝‍🧑</span>
              <span>Other Players</span>
            </div>
          </div>
          <div className="as-panel-body">
            <div className="other-players-grid">
            {otherPlayers.length > 0 ? (
              otherPlayers.map((seat) => (
                <div
                  key={seat.position}
                  className={`other-player-card ${
                    room?.data?.turn?.seatIndex === seat.position ? "other-player-card--turn" : ""
                  }`}
                >
                  <div className="player-name">
                    {getStableAnimalEmoji(`texas:${room_id}:seat:${seat.position}:player:${seat.playerName}`)} {seat.playerName}
                  </div>
                  <div className="player-badges-row" aria-label="Seat badges">
                    {seat.isSmallBlind && <span className="blind-badge">SB</span>}
                    {seat.isBigBlind && <span className="blind-badge">BB</span>}
                    {room?.data?.turn?.seatIndex === seat.position && (
                      <span className="turn-badge" aria-label="Current turn">
                        TURN
                      </span>
                    )}
                  </div>
                  {seatPresence.includes(seat.position) ? (
                    <>
                      <div className="player-bet">🎲 Bet: ${seat.bet || 0}</div>
                      {room.data?.initialChips?.[seat.position] !== undefined && (
                        <div className="player-total-bet">
                          💰 Total: ${(room.data.initialChips[seat.position] - seat.chips) || 0}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="player-chips player-chips--hidden">💤</div>
                  )}
                  <div className={`player-status ${seat.status === "ready" ? "ready" : seat.status === "waiting" ? "waiting" : ""}`}>
                    {seat.status}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-players">Waiting for other players...</div>
            )}
          </div>
          </div>
        </div>

        {/* Community Cards */}
        <div className="community-cards-section">
          <h3>Community Cards</h3>
          <div className="community-cards">
            {room.data.communityCards && room.data.communityCards.length > 0 ? (
              room.data.communityCards.map((card, index) => (
                <div key={index} className={`card ${isRedSuit(card) ? 'red-card' : ''}`}>
                  {card}
                </div>
              ))
            ) : (
              <div className="no-cards">No cards dealt yet</div>
            )}
          </div>
        </div>

        {/* Player's Hand */}
        <div className="my-hand-section">
          <h3>Your Hand</h3>
          <div className="my-hand">
            {mySeat.holeCards && mySeat.holeCards.length > 0 ? (
              mySeat.holeCards.map((card, index) => (
                <div key={index} className={`card my-card ${isRedSuit(card) ? 'red-card' : ''}`}>
                  {card}
                </div>
              ))
            ) : (
              <div className="no-cards">Waiting for cards...</div>
            )}
          </div>
        </div>

        {/* Seat Controls (placed under Your Hand section) */}
        {room.status === "playing" && mySeat.status !== "folded" && (
          <div className={`seat-controls-panel as-panel ${isMyTurn ? "seat-controls-panel--my-turn" : ""}`}>
            <div className="as-panel-header">
              <div className="as-panel-title">
                <span className="as-icon-badge" aria-hidden="true">{isMyTurn ? '⏱' : '🎮'}</span>
                <span>
                  {room.data?.turn && (isMyTurn
                    ? `Your turn — ${remainingSec ?? 0}s`
                    : `Waiting for Seat ${(room.data.turn.seatIndex ?? 0) + 1}`)}
                  {!room.data?.turn && 'Seat Controls'}
                </span>
              </div>
            </div>
            <div className="as-panel-body">
              {/* Turn timer bar */}
              {room.data?.turn && isMyTurn && (
                <div className="turn-timer-track" aria-label="Turn timer">
                  <div
                    className="turn-timer-fill"
                    style={{ width: `${remainingPct}%` }}
                  />
                </div>
              )}
              <div className="turn-action-row">
                <button
                  className={`btn btn-sm ${!isMyTurn || !canCheckNow ? 'as-btn-soft' : 'as-btn-success-gradient'}`}
                  onClick={handleCheck}
                  disabled={!isMyTurn || !canCheckNow}
                  title={!isMyTurn ? "Not your turn" : !canCheckNow ? "You must call/raise or fold" : "Check"}
                >
                  {getStableAnimalEmoji(`texas:${room_id}:seat:${seatIndexNum}:check`)} ✅ Check
                </button>
                <button
                  className={`btn btn-sm ${!canCallNow ? 'as-btn-soft' : 'as-btn-primary-gradient'}`}
                  onClick={handleCall}
                  disabled={!canCallNow}
                  title={!isMyTurn ? "Not your turn" : callAmountNeeded <= 0 ? "Nothing to call" : "Call"}
                >
                  {getStableAnimalEmoji(`texas:${room_id}:seat:${seatIndexNum}:call`)} 🎯 Call
                </button>
                <button
                  className="btn btn-sm as-btn-danger-gradient"
                  onClick={handleFold}
                  disabled={!isMyTurn}
                  title={!isMyTurn ? "Not your turn" : "Fold"}
                >
                  {getStableAnimalEmoji(`texas:${room_id}:seat:${seatIndexNum}:fold`)} 🃏 Fold
                </button>
                {!isMyTurn && (
                  <div className="turn-hint">
                    Waiting… (actions are enabled only on your turn)
                  </div>
                )}
              </div>

              <div className="betting-controls">
                <input
                  type="number"
                  className="form-control bet-input"
                  value={betAmount}
                  onChange={(e) => setBetAmount(e.target.value)}
                  placeholder="Enter amount"
                  min="1"
                  max={mySeat.chips}
                  disabled={!isMyTurn}
                />
                <button className="btn as-btn-info-gradient place-bet-btn" onClick={handlePlaceBet} disabled={!isMyTurn}>
                  {getStableAnimalEmoji(`texas:${room_id}:seat:${seatIndexNum}:place-bet`)} 💵 Bet
                </button>
              </div>
              <div className="quick-bet-buttons">
                <button className="btn btn-sm as-btn-soft" onClick={() => setBetAmount(Math.min(10, mySeat.chips))} disabled={!isMyTurn}>$10</button>
                <button className="btn btn-sm as-btn-soft" onClick={() => setBetAmount(Math.min(50, mySeat.chips))} disabled={!isMyTurn}>$50</button>
                <button className="btn btn-sm as-btn-soft" onClick={() => setBetAmount(Math.min(100, mySeat.chips))} disabled={!isMyTurn}>$100</button>
                <button className="btn btn-sm as-btn-soft" onClick={() => setBetAmount(mySeat.chips)} disabled={!isMyTurn}>All In</button>
              </div>
            </div>
          </div>
        )}

        {/* Folded Status Message */}
        {mySeat.status === 'folded' && (
          <div className="folded-message">
            <h3>✋ You have folded</h3>
            <p>Waiting for round to end...</p>
          </div>
        )}

        {/* Bottom Controls */}
        <div className="bottom-controls">
          <button className="btn as-btn-primary-gradient" onClick={() => setShowExerciseModal(true)}>
            {getStableAnimalEmoji(`texas:${room_id}:seat:${seatIndexNum}:exercise`)} 💪 Request Exercise (+1000 chips)
          </button>
          <button className="btn as-btn-primary-gradient" onClick={() => setShowDonationModal(true)}>
            {getStableAnimalEmoji(`texas:${room_id}:seat:${seatIndexNum}:donate`)} 💝 Donate for Chips
          </button>
          <button className="btn as-btn-soft" onClick={handleLeaveSeat}>
            {getStableAnimalEmoji(`texas:${room_id}:seat:${seatIndexNum}:leave`)} 🚪 Leave Seat
          </button>
        </div>
        </div>
        {/* End main content wrapper */}

        {/* Exercise Request Modal */}
        {showExerciseModal && (
          <div
            className="exercise-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Exercise"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setShowExerciseModal(false);
            }}
          >
            <div
              className="exercise-modal-box as-modal as-theme"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="as-modal-header-row">
                  <div className="as-modal-title-row">
                    <span className="as-modal-icon-badge" aria-hidden="true">💪</span>
                    <div className="truncate">
                      <h5 className="modal-title mb-0">Earn Chips Through Exercise</h5>
                      <div className="small" style={{ color: "rgba(0, 0, 0, 0.62)" }}>
                        Complete an exercise to earn <strong>$1000</strong> chips
                      </div>
                    </div>
                  </div>
                  <div className="as-modal-actions">
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setShowExerciseModal(false)}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-body">
                <div className="exercise-options-grid">
                  {exerciseOptions.map((exercise, index) => (
                    <button
                      type="button"
                      key={index}
                      className="exercise-option-card"
                      onClick={() => handleRequestExercise(exercise.name)}
                    >
                      <div className="exercise-icon">{exercise.icon}</div>
                      <div className="exercise-name">{exercise.name}</div>
                      <div className="exercise-description">{exercise.description}</div>
                      <div className="exercise-reward-badge">+$1000</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="modal-footer as-modal-footer">
                <button
                  type="button"
                  className="btn as-btn-soft"
                  onClick={() => setShowExerciseModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Donation Modal */}
        {showDonationModal && (
          <div
            className="exercise-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Donation"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setShowDonationModal(false);
            }}
          >
            <div
              className="exercise-modal-box donation-modal as-modal as-theme"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="as-modal-header-row">
                  <div className="as-modal-title-row">
                    <span className="as-modal-icon-badge" aria-hidden="true">💝</span>
                    <div className="truncate">
                      <h5 className="modal-title mb-0">Donate for Chips</h5>
                      <div className="small" style={{ color: "rgba(0, 0, 0, 0.62)" }}>
                        Support AnimalSplit and earn chips
                      </div>
                    </div>
                  </div>
                  <div className="as-modal-actions">
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setShowDonationModal(false)}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-body">
                <div className="donation-options-grid">
                  <button
                    type="button"
                    className="donation-option-card"
                    onClick={() => handleDonate(100, 500)}
                  >
                    <div className="donation-icon">🐵</div>
                    <div className="donation-amount">$1</div>
                    <div className="donation-description">Small token</div>
                    <div className="exercise-reward-badge">+500 chips</div>
                  </button>
                  <button
                    type="button"
                    className="donation-option-card"
                    onClick={() => handleDonate(500, 3000)}
                  >
                    <div className="donation-icon">🦁</div>
                    <div className="donation-amount">$5</div>
                    <div className="donation-description">Generous support</div>
                    <div className="exercise-reward-badge">+3000 chips</div>
                  </button>
                  <button
                    type="button"
                    className="donation-option-card"
                    onClick={() => handleDonate(1000, 7500)}
                  >
                    <div className="donation-icon">🐼</div>
                    <div className="donation-amount">$10</div>
                    <div className="donation-description">Amazing patron</div>
                    <div className="exercise-reward-badge">+7500 chips</div>
                  </button>
                </div>
                <div className="donation-note mt-3">
                  ✅ Tax-deductible receipt sent via email<br/>
                  🔒 Secure payment via Stripe<br/>
                  ✨ Chips awarded automatically after payment
                </div>
              </div>

              <div className="modal-footer as-modal-footer">
                <button
                  type="button"
                  className="btn as-btn-soft"
                  onClick={() => setShowDonationModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      )}
    </GameSeat>
  );
};

export default TexasSeat;
